import React, { useState, useEffect, useCallback } from 'react';
import { Badge, Btn, Modal, Loading, EmptyState, KpiCard } from '../components/ui';
import StopwatchModal from '../components/StopwatchModal';

const API = `${window.location.protocol}//${window.location.hostname}/api`;

function authHeaders() {
  const token = localStorage.getItem('lc_token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' };
}

function entryKey(slot) {
  return `${slot.slot_id}_${slot.line_design_item_id}`;
}

function calcBottleneck(operations) {
  let minOutput = Infinity;
  let bottleneckName = null;
  for (const op of operations) {
    const activeSlots = op.slots.filter(s => s.employee_id && !s.is_absent);
    if (!activeSlots.length) return op.operation_name;
    const avgSkill = activeSlots.reduce((a, s) => a + (parseFloat(s.skill_pct) || 0), 0) / activeSlots.length;
    if (avgSkill === 0) return op.operation_name;
    const effCT = parseFloat(op.sam) / (avgSkill / 100);
    const outputHr = (activeSlots.length * 60) / effCT;
    if (outputHr < minOutput) { minOutput = outputHr; bottleneckName = op.operation_name; }
  }
  return bottleneckName;
}

function groupSlots(slots) {
  const map = {};
  for (const s of slots) {
    const key = s.line_design_item_id;
    if (!map[key]) {
      map[key] = {
        line_design_item_id: key,
        operation_name: s.operation_name,
        operation_id: s.operation_id,
        sam: s.sam,
        skill_req_pct: s.skill_req_pct,
        is_bottleneck: s.is_bottleneck,
        op_row: s.op_row,
        is_clubbed: false,
        clubbed_partner_op_name: null,
        slots: []
      };
    }
    if (s.is_clubbed) {
      map[key].is_clubbed = true;
      map[key].clubbed_partner_op_name = s.clubbed_partner_op_name;
    }
    map[key].slots.push(s);
  }
  return Object.values(map).sort((a, b) => a.op_row - b.op_row);
}

// ─── Downtime modal: pick reason + enter minutes ──────────────────────────────
function DtModal({ open, onClose, reasons, existing, onSave }) {
  const [selectedReason, setSelectedReason] = useState(null);
  const [minutes, setMinutes] = useState('');

  useEffect(() => {
    if (open) {
      setSelectedReason(existing?.reason_id ? reasons.find(r => r.id === existing.reason_id) || null : null);
      setMinutes(existing?.minutes ? String(existing.minutes) : '');
    }
  }, [open, existing, reasons]);

  const handleClose = () => { setSelectedReason(null); setMinutes(''); onClose(); };
  const handleSave = () => {
    if (!selectedReason || !minutes || parseInt(minutes) <= 0) return;
    onSave(selectedReason, parseInt(minutes));
    setSelectedReason(null); setMinutes('');
  };

  return (
    <Modal open={open} onClose={handleClose} title="Log downtime" width={400}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, color: '#666666', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase' }}>Select reason</div>
          {reasons.map(r => (
            <div key={r.id} onClick={() => setSelectedReason(r)} style={{
              padding: '10px 12px', borderRadius: 6, marginBottom: 5, cursor: 'pointer',
              border: selectedReason?.id === r.id ? '1.5px solid #185FA5' : '0.5px solid rgba(0,0,0,0.10)',
              background: selectedReason?.id === r.id ? '#E6F1FB' : '#fff',
              fontSize: 13, fontWeight: 500,
              color: selectedReason?.id === r.id ? '#185FA5' : '#1a1a1a'
            }}>{r.name}</div>
          ))}
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#666666', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase' }}>Duration (minutes)</div>
          <input
            type="number" min="1" value={minutes}
            onChange={e => setMinutes(e.target.value)}
            placeholder="Enter minutes…"
            style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid rgba(0,0,0,0.10)', fontSize: 13, boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={handleClose} style={{ padding: '7px 16px', borderRadius: 6, border: '0.5px solid rgba(0,0,0,0.10)', background: '#fff', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
          <button onClick={handleSave} disabled={!selectedReason || !minutes || parseInt(minutes) <= 0} style={{
            padding: '7px 16px', borderRadius: 6, border: 'none',
            cursor: (selectedReason && minutes && parseInt(minutes) > 0) ? 'pointer' : 'not-allowed',
            background: (selectedReason && minutes && parseInt(minutes) > 0) ? '#185FA5' : 'rgba(0,0,0,0.10)',
            color: '#fff', fontSize: 13, fontWeight: 600
          }}>Save</button>
        </div>
      </div>
    </Modal>
  );
}


// ─── End-of-day WIP correction modal ─────────────────────────────────────────
function WipCorrectionModal({ open, onClose, operations, currentWip, onSave }) {
  const [overrides, setOverrides] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const init = {};
      operations.forEach(op => { init[op.line_design_item_id] = String(currentWip[op.line_design_item_id] ?? ''); });
      setOverrides(init);
    }
  }, [open, operations, currentWip]);

  const handleSave = async () => {
    setSaving(true);
    const entries = Object.entries(overrides).map(([ldiId, qty]) => ({
      line_design_item_id: parseInt(ldiId),
      wip_qty: parseFloat(qty) || 0
    }));
    await onSave(entries);
    setSaving(false);
  };

  return (
    <Modal open={open} onClose={onClose} title="End-of-day WIP correction" width={480}>
      <div style={{ marginBottom: 12, fontSize: 12, color: '#666666' }}>
        Override closing WIP for the last saved hour. These values carry forward as tomorrow's opening WIP.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 420, overflowY: 'auto' }}>
        {operations.map(op => (
          <div key={op.line_design_item_id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 10px', borderRadius: 6, border: '0.5px solid rgba(0,0,0,0.10)', background: '#fafafa' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{op.operation_name}</div>
              <div style={{ fontSize: 10, color: '#666666' }}>Auto-calc: {currentWip[op.line_design_item_id] ?? 0}</div>
            </div>
            <input
              type="number" min="0"
              value={overrides[op.line_design_item_id] ?? ''}
              onChange={e => setOverrides(prev => ({ ...prev, [op.line_design_item_id]: e.target.value }))}
              placeholder="0"
              style={{ width: 80, padding: '6px 8px', borderRadius: 5, border: '1.5px solid #185FA5', fontSize: 13, fontWeight: 600, textAlign: 'center' }}
            />
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <button onClick={onClose} style={{ padding: '7px 16px', borderRadius: 6, border: '0.5px solid rgba(0,0,0,0.10)', background: '#fff', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
        <button onClick={handleSave} disabled={saving} style={{ padding: '7px 16px', borderRadius: 6, border: 'none', background: '#185FA5', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
          {saving ? 'Saving…' : 'Save WIP'}
        </button>
      </div>
    </Modal>
  );
}

// ─── Line Selector Landing ────────────────────────────────────────────────────
function LineSelector({ deployments, onSelect, error, filterBar }) {
  const [search, setSearch] = useState('');
  const filtered = deployments.filter(d =>
    String(d.line_number).toLowerCase().includes(search.toLowerCase()) ||
    d.style_number?.toLowerCase().includes(search.toLowerCase()) ||
    d.buyer?.toLowerCase().includes(search.toLowerCase())
  );
  return (
    <div style={{ padding: 16, background: '#EFEDE8', minHeight: '100vh' }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 18, fontWeight: 700 }}>Hourly Reporting</div>
        <div style={{ fontSize: 12, color: '#666666', marginTop: 2 }}>Select a line to begin</div>
      </div>
      {error && <div style={{ background: '#FCEBEB', border: '1px solid #A32D2D', borderRadius: 7, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#A32D2D' }}>{error}</div>}
      {filterBar}
      <div style={{ marginBottom: 16 }}>
        <input className="inp" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by line number, style or buyer…" style={{ width: '100%', boxSizing: 'border-box' }} />
      </div>
      {filtered.length === 0 && <EmptyState message={deployments.length === 0 ? "No active deployments found." : "No lines match your search."} />}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
        {filtered.map(d => (
          <div key={d.id} onClick={() => onSelect(String(d.id))} style={{ background: '#fff', borderRadius: 10, padding: '16px 18px', border: '0.5px solid rgba(0,0,0,0.10)', cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.10)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)'}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>Line {d.line_number}</div>
              <Badge type="green">Active</Badge>
            </div>
            <div style={{ fontSize: 13, color: '#1a1a1a', marginBottom: 4 }}>{d.style_number}{d.buyer ? ` — ${d.buyer}` : ''}</div>
            <div style={{ fontSize: 11, color: '#666666' }}>{d.shift_name} · {d.manpower || '—'} operators</div>
            <div style={{ marginTop: 12, paddingTop: 10, borderTop: '0.5px solid rgba(0,0,0,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 12, color: '#185FA5', fontWeight: 600 }}>{Math.round(d.plan_efficiency || d.efficiency || 0)}% efficiency</span>
              <span style={{ fontSize: 12, color: '#666666' }}>Open →</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function HourlyReporting() {
  const [divisions, setDivisions] = useState([]);
  const [factories, setFactories] = useState([]);
  const [allFactories, setAllFactories] = useState([]);
  const [selectedDivision, setSelectedDivision] = useState('');
  const [selectedFactory, setSelectedFactory] = useState('');
  const [selectedShift, setSelectedShift] = useState('');
  const [deployments, setDeployments] = useState([]);
  const [depId, setDepId] = useState('');
  const [dep, setDep] = useState(null);
  const [operations, setOperations] = useState([]);
  const [hourSlots, setHourSlots] = useState([]);
  const [lastOpRow, setLastOpRow] = useState(null);
  const [selectedHour, setSelectedHour] = useState(1);
  const [lastSavedHour, setLastSavedHour] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const [fetchError, setFetchError] = useState('');
  const [summary, setSummary] = useState(null);

  // Per-hour data (reset on hour change)
  const [entries, setEntries] = useState({});        // { slotId: { quantity_produced, cycle_time } }
  const [wipMap, setWipMap] = useState({});           // { ldi_id: closing_wip } for current hour
  const [openingWip, setOpeningWip] = useState({});   // { ldi_id: opening_wip } for current hour (read-only)
  const [loadingMap, setLoadingMap] = useState({});   // { ldi_id: loading_qty } persists across hours
  const [dtMap, setDtMap] = useState({});             // { slotId: { reason_id, reason_name, minutes } }

  const [dtReasons, setDtReasons] = useState([]);
  const [dtModal, setDtModal] = useState({ open: false, slot: null });
  const [stopwatchModal, setStopwatchModal] = useState({ open: false, slot: null });
  const [wipError, setWipError] = useState('');
  const [nph, setNph] = useState({ output: '', operators: '', minutes: '' });
  const [nphOpen, setNphOpen] = useState(false);
  const [wipCorrOpen, setWipCorrOpen] = useState(false);

  useEffect(() => {
    // PWA — no page title element
  }, []);

  useEffect(() => {
    if (selectedDivision) {
      setFactories(allFactories.filter(f => String(f.division_id) === String(selectedDivision)));
      setSelectedFactory('');
    } else {
      setFactories(allFactories);
    }
  }, [selectedDivision, allFactories]);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/deployment`, { headers: authHeaders() }).then(r => r.json()),
      fetch(`${API}/divisions`, { headers: authHeaders() }).then(r => r.json()),
      fetch(`${API}/factories`, { headers: authHeaders() }).then(r => r.json()),
    ]).then(([depData, divData, facData]) => {
      const active = (Array.isArray(depData) ? depData : []).filter(x => x.status?.toLowerCase() === 'active');
      setDeployments(active);
      if (Array.isArray(divData)) setDivisions(divData);
      if (Array.isArray(facData)) { setFactories(facData); setAllFactories(facData); }
    }).catch(err => setFetchError(`Network error: ${err.message}`));
  }, []);

  const loadSummary = useCallback((id) => {
    fetch(`${API}/hourly/${id}/summary`, { headers: authHeaders() })
      .then(r => r.json()).then(setSummary).catch(() => {});
  }, []);

  const loadDeployment = useCallback((id) => {
    if (!id) return;
    setLoading(true); setFetchError('');
    Promise.all([
      fetch(`${API}/hourly/${id}`, { headers: authHeaders() }).then(r => r.json()),
      fetch(`${API}/hourly/${id}/downtime-reasons`, { headers: authHeaders() }).then(r => r.json()),
      fetch(`${API}/hourly/${id}/summary`, { headers: authHeaders() }).then(r => r.json())
    ]).then(([depData, reasons, sumData]) => {
      if (depData.error) { setFetchError(depData.error); setLoading(false); return; }
      setDep(depData);
      setOperations(groupSlots(depData.slots || []));
      setHourSlots(depData.hour_slots || []);
      setLastOpRow(depData.last_op_row);
      const curHour = depData.current_hour || 1;
      const lastSaved = depData.last_saved_hour || 0;
      setSelectedHour(curHour);
      setLastSavedHour(lastSaved);
      setDtReasons(Array.isArray(reasons) ? reasons : []);
      setSummary(sumData);
      setLoadingMap(depData.loading_map || {});
      if (depData.nph) setNph({ output: depData.nph.output || '', operators: depData.nph.operators || '', minutes: depData.nph.minutes || '' });

      // H1 opening WIP = yesterday's H8 closing WIP
      if (curHour === 1) {
        setOpeningWip(depData.yesterday_wip || {});
        setWipMap({});
      }
      setLoading(false);
    }).catch(err => { setFetchError(`Error: ${err.message}`); setLoading(false); });
  }, []);

  // Load entries for a specific hour
  const loadHourEntries = useCallback((id, hour) => {
    fetch(`${API}/hourly/${id}/entries/${hour}`, { headers: authHeaders() })
      .then(r => r.json())
      .then(data => {
        const rows = data.entries || [];
        const map = {};
        rows.forEach(r => {
          map[`${r.slot_id}_${r.line_design_item_id}`] = { quantity_produced: r.quantity_produced, cycle_time: r.cycle_time };
        });
        setEntries(map);
        // closing WIP of this hour = opening WIP of next
        setWipMap(data.wip_map || {});
        // loading_map deliberately NOT merged — loading resets to zero each hour
        // DT for this hour
        setDtMap(data.dt_map || {});
      }).catch(() => {});
  }, []);

  // When switching to hour H, opening WIP = closing WIP of H-1
  const loadOpeningWip = useCallback((id, hour, yesterdayWip) => {
    if (hour === 1) {
      setOpeningWip(yesterdayWip || {});
      return;
    }
    fetch(`${API}/hourly/${id}/entries/${hour - 1}`, { headers: authHeaders() })
      .then(r => r.json())
      .then(data => { setOpeningWip(data.wip_map || {}); })
      .catch(() => setOpeningWip({}));
  }, []);

  const handleSelect = (id) => {
    setDepId(id); setEntries({}); setDtMap({}); setWipMap({}); setOpeningWip({});
    loadDeployment(id);
  };

  const handleBack = () => {
    setDep(null); setDepId(''); setOperations([]);
    // PWA — no page title element
  };

  const handleHourChange = (h) => {
    // Block if previous hour not saved
    if (h > 1 && !(summary?.hours || []).find(x => x.hour_number === h - 1)) {
      setWipError(`Save Hour ${h - 1} before entering Hour ${h}.`);
      return;
    }
    setWipError('');
    setSelectedHour(h);
    setEntries({}); setDtMap({}); setWipMap({}); setLoadingMap({});
    loadOpeningWip(depId, h, dep?.yesterday_wip);
    loadHourEntries(depId, h);
  };

  // Load entries when dep first loads
  useEffect(() => {
    if (depId && dep) {
      loadOpeningWip(depId, selectedHour, dep.yesterday_wip);
      loadHourEntries(depId, selectedHour);
    }
  }, [depId]); // eslint-disable-line

  const isHourReadOnly = (h) => h <= lastSavedHour;

  const handleEntryChange = (key, field, value) => {
    setEntries(prev => ({ ...prev, [key]: { ...(prev[key] || {}), [field]: value } }));
  };

  const handleLoadingChange = (ldiId, value) => {
    setLoadingMap(prev => ({ ...prev, [ldiId]: value }));
  };

  // Output for an operation in current hour
  const getOpOutput = (op) => {
    return op.slots.reduce((sum, slot) => {
      const e = entries[entryKey(slot)] || {};
      return sum + (parseFloat(e.quantity_produced) || 0);
    }, 0);
  };

  // Closing WIP for current hour
  // Op1:  closing = loading - output
  // OpN:  closing = opening_wip[opN] + prevOp_output - current_output
  const computeClosingWip = (op, opIndex, allOps) => {
    const output = getOpOutput(op);
    if (opIndex === 0) {
      const loading = parseFloat(loadingMap[op.line_design_item_id]) || 0;
      const myOpeningWip = parseFloat(openingWip[op.line_design_item_id]) || 0;
      return myOpeningWip + loading - output;
    } else {
      const prevOp = allOps[opIndex - 1];
      const prevOutput = getOpOutput(prevOp);
      const myOpeningWip = parseFloat(openingWip[op.line_design_item_id]) || 0;
      return myOpeningWip + prevOutput - output;
    }
  };

  const handleDtSave = (slot, reason, minutes) => {
    setDtMap(prev => ({
      ...prev,
      [slot.slot_id]: { reason_id: reason.id, reason_name: reason.name, minutes }
    }));
    setDtModal({ open: false, slot: null });
  };

  const handleDtClear = (slotId) => {
    setDtMap(prev => { const n = { ...prev }; delete n[slotId]; return n; });
  };

  const handleSave = async () => {
    setWipError('');

    // Validate OpN output <= opening_wip + prevOp_output
    for (let i = 1; i < operations.length; i++) {
      const op = operations[i];
      const prevOp = operations[i - 1];
      const opOutput = getOpOutput(op);
      const prevOutput = getOpOutput(prevOp);
      const myOpeningWip = parseFloat(openingWip[op.line_design_item_id]) || 0;
      const maxAllowed = myOpeningWip + prevOutput;
      if (opOutput > maxAllowed) {
        setWipError(`"${op.operation_name}" output (${opOutput}) exceeds opening WIP + prev op output (${maxAllowed} pcs).`);
        return;
      }
    }

    setSaving(true);

    // Production entries
    const payload = [];
    operations.forEach(op => {
      op.slots.forEach(slot => {
        const e = entries[entryKey(slot)] || {};
        payload.push({
          slot_id: slot.slot_id,
          employee_id: slot.employee_id,
          line_design_item_id: slot.line_design_item_id,
          operation_id: slot.operation_id,
          quantity_produced: parseFloat(e.quantity_produced) || 0,
          cycle_time: parseFloat(e.cycle_time) || null
        });
      });
    });

    // WIP entries (closing WIP for this hour)
    const wipEntries = operations.map((op, idx) => ({
      line_design_item_id: op.line_design_item_id,
      wip_qty: Math.max(0, computeClosingWip(op, idx, operations))
    }));

    // Loading entries (all ops that have loading qty)
    const loadingEntries = Object.entries(loadingMap)
      .filter(([, qty]) => qty !== '' && qty !== undefined && qty !== null)
      .map(([ldiId, qty]) => ({ line_design_item_id: parseInt(ldiId), loading_qty: parseFloat(qty) || 0 }));

    // DT entries
    const dtEntries = Object.entries(dtMap)
      .filter(([, dt]) => dt.reason_id && dt.minutes > 0)
      .map(([slotId, dt]) => ({ slot_id: parseInt(slotId), reason_id: dt.reason_id, minutes: dt.minutes }));

    const nphPayload = { output: parseInt(nph.output) || 0, operators: parseInt(nph.operators) || 0, minutes: parseInt(nph.minutes) || 0 };

    const r = await fetch(`${API}/hourly/${depId}/save`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({
        hour_number: selectedHour,
        entries: payload,
        wip_entries: wipEntries,
        loading_entries: loadingEntries,
        dt_entries: dtEntries,
        nph: nphPayload
      })
    });

    if (r.ok) {
      setSavedMsg('Saved ✓');
      setLastSavedHour(selectedHour);
      setTimeout(() => setSavedMsg(''), 2000);
      loadSummary(depId);
    } else {
      const d = await r.json();
      setSavedMsg(`Error: ${d.error}`);
      setTimeout(() => setSavedMsg(''), 4000);
    }
    setSaving(false);
  };

  const handleWipCorrSave = async (entries) => {
    const r = await fetch(`${API}/hourly/${depId}/wip-override`, {
      method: 'PATCH', headers: authHeaders(),
      body: JSON.stringify({ hour_number: lastSavedHour, wip_overrides: entries })
    });
    if (r.ok) { setWipCorrOpen(false); }
    else { const d = await r.json(); alert('WIP save error: ' + d.error); }
  };

  const totalOutput = (summary?.hours || []).reduce((s, h) => s + (h.output || 0), 0);
  const totalDtMins = (summary?.hours || []).reduce((s, h) => s + (h.dt_minutes || 0), 0);
  const currentHourSummary = (summary?.hours || []).find(h => h.hour_number === selectedHour);
  const currentHourLabel = hourSlots.find(s => s.hour === selectedHour)?.label || `H${selectedHour}`;
  const deployedBottleneck = calcBottleneck(operations);
  const totalDeployed = dep ? (dep.manpower || 0) : 0;
  const totalSam = dep ? parseFloat(dep.total_sam || 0) : 0;
  // Running efficiency = (output * SAM) / (operators * hoursElapsed * 60) * 100
  // hoursElapsed = last saved hour (not hours with data)
  const hoursElapsed = Math.max(lastSavedHour, 1);
  const runningEfficiency = (totalDeployed > 0 && totalOutput > 0 && totalSam > 0)
    ? Math.round((totalOutput * totalSam) / (totalDeployed * hoursElapsed * 60) * 100 * 10) / 10
    : 0;

  const readOnly = isHourReadOnly(selectedHour);

  const filteredDeployments = deployments.filter(d => {
    if (selectedDivision && String(d.division_id) !== String(selectedDivision)) return false;
    if (selectedFactory && String(d.factory_id) !== String(selectedFactory)) return false;
    if (selectedShift && d.shift_name !== selectedShift) return false;
    return true;
  });

  // Distinct shift names available, for the Shift filter dropdown
  const shiftOptions = [...new Set(deployments.map(d => d.shift_name).filter(Boolean))].sort();

  if (!dep && !loading) return (
    <LineSelector
      deployments={filteredDeployments}
      onSelect={handleSelect}
      error={fetchError}
      filterBar={
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
          <select value={selectedDivision} onChange={e => setSelectedDivision(e.target.value)}
            style={{ padding: '6px 12px', borderRadius: 6, border: '0.5px solid rgba(0,0,0,0.10)', background: '#fff', fontSize: 12, cursor: 'pointer' }}>
            <option value="">All divisions</option>
            {divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <select value={selectedFactory} onChange={e => setSelectedFactory(e.target.value)}
            style={{ padding: '6px 12px', borderRadius: 6, border: '0.5px solid rgba(0,0,0,0.10)', background: '#fff', fontSize: 12, cursor: 'pointer' }}>
            <option value="">All factories</option>
            {factories.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
          <select value={selectedShift} onChange={e => setSelectedShift(e.target.value)}
            style={{ padding: '6px 12px', borderRadius: 6, border: '0.5px solid rgba(0,0,0,0.10)', background: '#fff', fontSize: 12, cursor: 'pointer' }}>
            <option value="">All shifts</option>
            {shiftOptions.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      }
    />
  );
  if (loading) return <div style={{ padding: 16, background: '#EFEDE8', minHeight: '100vh' }}><Loading /></div>;

  if (dep && !dep.has_daily_balancing) {
    return (
      <div style={{ padding: 16, background: '#EFEDE8', minHeight: '100vh' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <button onClick={handleBack} className="btn btn-s" style={{ color: '#185FA5', borderColor: '#E6F1FB', background: '#E6F1FB' }}>← Lines</button>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>Hourly Reporting</div>
            <div style={{ fontSize: 12, color: '#666666', marginTop: 2 }}>
              Line {dep.line_number} · {dep.shift_name} · {dep.style_number}{dep.buyer ? ` — ${dep.buyer}` : ''}
            </div>
          </div>
        </div>
        <div style={{ background: '#fff', border: '1px solid #854F0B', borderRadius: 10, padding: '40px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>⚠</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#854F0B', marginBottom: 8 }}>Daily Balancing not saved for today</div>
          <div style={{ fontSize: 13, color: '#666666', maxWidth: 440, margin: '0 auto' }}>
            Hourly production can't be recorded until today's Daily Balancing (attendance, absences, replacements) is saved for this line. Go to Daily Balancing, confirm today's staffing, and save it — then this screen will unlock.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 16, background: '#EFEDE8', minHeight: '100vh' }}>

      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: '1 1 auto', minWidth: 0 }}>
          <button onClick={handleBack} className="btn btn-s" style={{ color: '#185FA5', borderColor: '#E6F1FB', background: '#E6F1FB', flexShrink: 0 }}>← Lines</button>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 700 }}>Hourly Reporting</div>
            <div style={{ fontSize: 12, color: '#666666', marginTop: 2 }}>
              Line {dep.line_number} · {dep.shift_name} · {dep.style_number}{dep.buyer ? ` — ${dep.buyer}` : ''}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {lastSavedHour > 0 && (
            <button onClick={() => setWipCorrOpen(true)} style={{
              padding: '7px 14px', borderRadius: 6, border: '0.5px solid #854F0B', background: '#fff',
              color: '#854F0B', fontSize: 12, fontWeight: 600, cursor: 'pointer'
            }}>WIP Correction</button>
          )}
          <button onClick={() => setNphOpen(true)} style={{
            padding: '7px 14px', borderRadius: 6, border: '0.5px solid #185FA5', background: nph.output > 0 ? '#185FA5' : '#fff',
            color: nph.output > 0 ? '#fff' : '#185FA5', fontSize: 12, fontWeight: 600, cursor: 'pointer'
          }}>
            NPH {nph.output > 0 ? `· ${nph.output} pcs` : ''}
          </button>
          {!readOnly && (
            <Btn variant="primary" size="sm" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : savedMsg || `Save H${selectedHour}`}
            </Btn>
          )}
          {readOnly && (
            <div style={{ padding: '7px 14px', borderRadius: 6, background: '#F7F6F3', fontSize: 12, fontWeight: 600, color: '#666666' }}>
              H{selectedHour} saved — read only
            </div>
          )}
        </div>
      </div>

      {fetchError && <div style={{ background: '#FCEBEB', border: '1px solid #A32D2D', borderRadius: 7, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#A32D2D' }}>{fetchError}</div>}

      {/* KPI strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(115px, 1fr))', gap: 10, marginBottom: 18 }}>
        <KpiCard label="Day target" value={dep.target_output_day || summary?.target_output_day || '—'} type="default" />
        <KpiCard label="Hourly target" value={dep.target_output_hr || summary?.target_output_hr || '—'} type="default" />
        <KpiCard label="Output till now" value={totalOutput} type="blue" />
        <KpiCard label={`${currentHourLabel} output`} value={currentHourSummary?.output || 0} type="default" />
        <KpiCard label="DT mins today" value={totalDtMins} type={totalDtMins > 0 ? 'red' : 'default'} />
        <KpiCard label="Running efficiency" value={`${runningEfficiency}%`} type={runningEfficiency >= 75 ? 'green' : runningEfficiency >= 60 ? 'amber' : 'red'} />
      </div>

      {/* Hour selector tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 18, flexWrap: 'wrap' }}>
        {hourSlots.map(slot => {
          const hEntry = (summary?.hours || []).find(x => x.hour_number === slot.hour);
          const isCurrent = slot.hour === selectedHour;
          const isSaved = slot.hour <= lastSavedHour;
          const isBlocked = slot.hour > 1 && !(summary?.hours || []).find(x => x.hour_number === slot.hour - 1) && slot.hour !== selectedHour;
          return (
            <button key={slot.hour} onClick={() => handleHourChange(slot.hour)}
              disabled={isBlocked}
              style={{
                padding: '6px 14px', borderRadius: 6, border: isSaved && !isCurrent ? '1.5px solid #3B6D11' : 'none',
                fontSize: 12, fontWeight: 600,
                cursor: isBlocked ? 'not-allowed' : 'pointer',
                opacity: isBlocked ? 0.4 : 1,
                background: isCurrent ? '#185FA5' : isSaved ? '#EAF3DE' : '#F7F6F3',
                color: isCurrent ? '#fff' : isSaved ? '#3B6D11' : '#666666'
              }}>
              {slot.label}{hEntry && !isCurrent ? ` · ${hEntry.output}` : ''}
              {isSaved && !isCurrent ? ' ✓' : ''}
            </button>
          );
        })}
      </div>

      {readOnly && (
        <div style={{ background: '#EFF6FF', border: '1px solid #185FA5', borderRadius: 7, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#185FA5', fontWeight: 500 }}>
          🔒 Hour {selectedHour} is saved. Viewing in read-only mode.
        </div>
      )}

      {/* NPH modal */}
      {nphOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 340, boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>NPH — Non-productive hour</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[['Output', 'output'], ['Operators', 'operators'], ['Minutes', 'minutes']].map(([label, key]) => (
                <div key={key}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#666666', marginBottom: 4 }}>{label.toUpperCase()}</div>
                  <input type="number" min="0" value={nph[key]}
                    onChange={e => setNph(p => ({ ...p, [key]: e.target.value }))}
                    placeholder="0"
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1.5px solid rgba(0,0,0,0.10)', fontSize: 14, textAlign: 'center', boxSizing: 'border-box' }} />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
              <button onClick={() => setNphOpen(false)} style={{ padding: '7px 16px', borderRadius: 6, border: '0.5px solid rgba(0,0,0,0.10)', background: '#fff', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={() => setNphOpen(false)} style={{ padding: '7px 16px', borderRadius: 6, border: 'none', background: '#185FA5', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* WIP Correction Modal */}
      <WipCorrectionModal
        open={wipCorrOpen}
        onClose={() => setWipCorrOpen(false)}
        operations={operations}
        currentWip={wipMap}
        onSave={handleWipCorrSave}
      />

      {/* WIP validation error */}
      {wipError && (
        <div style={{ background: '#FCEBEB', border: '1px solid #A32D2D', borderRadius: 7, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#A32D2D', fontWeight: 500 }}>
          ⛔ {wipError}
        </div>
      )}

      {/* DT modal */}
      <DtModal
        open={dtModal.open}
        onClose={() => setDtModal({ open: false, slot: null })}
        reasons={dtReasons}
        existing={dtModal.slot ? dtMap[dtModal.slot.slot_id] : null}
        onSave={(reason, minutes) => handleDtSave(dtModal.slot, reason, minutes)}
      />

      {/* Stopwatch modal — applies its result through the exact same
          handleEntryChange path as typing a cycle time manually */}
      <StopwatchModal
        open={stopwatchModal.open}
        onClose={() => setStopwatchModal({ open: false, slot: null })}
        subtitle={stopwatchModal.slot ? `${stopwatchModal.slot.emp_name} — cycle time` : ''}
        onApply={(value) => {
          if (stopwatchModal.slot) handleEntryChange(entryKey(stopwatchModal.slot), 'cycle_time', value);
        }}
      />

      {/* Production — stacked cards, no fixed-width table */}
      {operations.length === 0 && (
        <div style={{ background: '#fff', borderRadius: 8, border: '0.5px solid rgba(0,0,0,0.10)' }}>
          <EmptyState message="No deployed operators found. Save daily balancing first." />
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {operations.map((op, opIndex) => {
          const isLastOp = op.op_row === lastOpRow;
          const isDeployedBottleneck = op.operation_name === deployedBottleneck;
          const closingWip = computeClosingWip(op, opIndex, operations);
          const myOpeningWip = parseFloat(openingWip[op.line_design_item_id]) || 0;
          const prevOpOutput = opIndex > 0 ? getOpOutput(operations[opIndex - 1]) : 0;

          return (
            <div key={op.line_design_item_id} style={{
              background: '#fff', borderRadius: 8, overflow: 'hidden',
              border: isDeployedBottleneck ? '1.5px solid #A32D2D' : '0.5px solid rgba(0,0,0,0.10)'
            }}>
              {/* Operation header */}
              <div style={{
                padding: '10px 12px',
                background: isDeployedBottleneck ? '#FCEBEB' : isLastOp ? '#EAF3DE' : '#F7F6F3',
              }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 8px', marginBottom: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: 13.5, flex: '1 1 100%' }}>{op.operation_name}</span>
                  {op.is_clubbed && (
                    op.clubbed_partner_op_name && op.clubbed_partner_op_name !== op.operation_name ? (
                      <Badge type="blue" size="xs">🔗 Clubbed with {op.clubbed_partner_op_name}</Badge>
                    ) : (
                      <Badge type="blue" size="xs">🔗 Shared operator</Badge>
                    )
                  )}
                  {isDeployedBottleneck && <Badge type="red" size="xs">⚠ Bottleneck</Badge>}
                  {isLastOp && <Badge type="green" size="xs">Line output</Badge>}
                  <span style={{ fontSize: 11, color: '#666666', marginLeft: 'auto' }}>SAM {parseFloat(op.sam).toFixed(3)}</span>
                </div>

                {/* Op-level stats: Loading / Output / Closing WIP */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 9.5, fontWeight: 700, color: '#666666', textTransform: 'uppercase', marginBottom: 3 }}>Loading</div>
                    {readOnly ? (
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#666666' }}>{loadingMap[op.line_design_item_id] || '—'}</div>
                    ) : (
                      <input
                        type="number" min="0"
                        value={loadingMap[op.line_design_item_id] || ''}
                        onChange={e => handleLoadingChange(op.line_design_item_id, e.target.value)}
                        placeholder="0"
                        style={{ width: '100%', padding: '7px 8px', borderRadius: 5, border: '1.5px solid rgba(0,0,0,0.15)', fontSize: 14, fontWeight: 600, textAlign: 'center', background: '#F8F9FA', boxSizing: 'border-box' }}
                      />
                    )}
                  </div>
                  <div>
                    <div style={{ fontSize: 9.5, fontWeight: 700, color: '#666666', textTransform: 'uppercase', marginBottom: 3 }}>Op output</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: isLastOp ? '#3B6D11' : '#185FA5' }}>{getOpOutput(op)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 9.5, fontWeight: 700, color: '#666666', textTransform: 'uppercase', marginBottom: 3 }}>Closing WIP</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: closingWip < 0 ? '#A32D2D' : '#3B6D11' }}>
                      {Math.max(0, closingWip)}
                    </div>
                    {opIndex > 0 && (
                      <div style={{ fontSize: 9, color: '#666666', fontWeight: 400 }}>OW:{myOpeningWip} P:{prevOpOutput}</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Operator rows — one compact row per operator, not a stacked card */}
              {op.slots.map((slot, idx) => {
                const entry = entries[entryKey(slot)] || {};
                const skillPct = Math.round(parseFloat(slot.skill_pct) || 0);
                const slotDt = dtMap[slot.slot_id];

                return (
                  <div key={slot.slot_id} style={{
                    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10,
                    padding: '8px 12px',
                    borderTop: '0.5px solid rgba(0,0,0,0.10)', background: idx % 2 === 0 ? '#fff' : '#fafafa'
                  }}>
                    {/* Name + skill badge */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '1 1 160px', minWidth: 0 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{slot.emp_name}</div>
                        <div style={{ fontSize: 10, color: '#666666' }}>{slot.emp_id}</div>
                      </div>
                      <Badge type={skillPct >= 80 ? 'green' : skillPct >= 60 ? 'amber' : 'red'}>{skillPct}%</Badge>
                    </div>

                    {/* Hourly production */}
                    <div style={{ flex: '0 1 110px' }}>
                      {readOnly ? (
                        <div style={{ textAlign: 'center', fontSize: 16, fontWeight: 700, color: isLastOp ? '#3B6D11' : '#185FA5' }}>
                          {entry.quantity_produced || 0}
                        </div>
                      ) : (
                        <input
                          type="text" inputMode="numeric" pattern="[0-9]*"
                          value={entry.quantity_produced === 0 || entry.quantity_produced ? String(entry.quantity_produced) : ''}
                          onChange={e => {
                            const raw = e.target.value.replace(/[^0-9]/g, '');
                            handleEntryChange(entryKey(slot), 'quantity_produced', raw === '' ? '' : parseInt(raw, 10));
                          }}
                          placeholder="Prd"
                          style={{
                            width: '100%', padding: '7px 6px', borderRadius: 6, textAlign: 'center', boxSizing: 'border-box',
                            border: `1.5px solid ${isLastOp ? '#3B6D11' : '#185FA5'}`,
                            background: isLastOp ? '#EAF3DE' : '#EFF6FF',
                            fontSize: 14, fontWeight: 700, color: isLastOp ? '#3B6D11' : '#185FA5'
                          }}
                        />
                      )}
                    </div>

                    {/* Cycle time + stopwatch */}
                    <div style={{ flex: '0 1 110px', display: 'flex', alignItems: 'center', gap: 4 }}>
                      {readOnly ? (
                        <div style={{ textAlign: 'center', fontSize: 14, fontWeight: 600, color: '#854F0B', flex: 1 }}>{entry.cycle_time || '—'}</div>
                      ) : (
                        <>
                          <input
                            type="text" inputMode="decimal"
                            value={entry.cycle_time !== undefined && entry.cycle_time !== null && entry.cycle_time !== '' ? String(entry.cycle_time) : ''}
                            onChange={e => {
                              const raw = e.target.value.replace(/[^0-9.]/g, '');
                              handleEntryChange(entryKey(slot), 'cycle_time', raw);
                            }}
                            placeholder="CT"
                            style={{ flex: 1, minWidth: 0, padding: '7px 6px', borderRadius: 6, textAlign: 'center', border: '1.5px solid #854F0B', background: '#FFFBEB', fontSize: 14, fontWeight: 600, color: '#854F0B', boxSizing: 'border-box' }}
                          />
                          <button onClick={() => setStopwatchModal({ open: true, slot })}
                            style={{ fontSize: 16, padding: '2px 4px', border: 'none', background: 'transparent', color: '#185FA5', cursor: 'pointer', flexShrink: 0 }}
                            title="Time it with a stopwatch">⏱</button>
                        </>
                      )}
                    </div>

                    {/* Downtime */}
                    <div style={{ flex: '1 1 140px', minWidth: 0 }}>
                      {readOnly ? (
                        slotDt ? (
                          <div style={{ fontSize: 12, color: '#A32D2D', fontWeight: 600 }}>
                            {slotDt.minutes} min <span style={{ fontSize: 10, color: '#666666', fontWeight: 400 }}>({slotDt.reason_name})</span>
                          </div>
                        ) : <div style={{ fontSize: 13, color: '#666666' }}>—</div>
                      ) : slotDt ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <div style={{ fontSize: 12, color: '#A32D2D', fontWeight: 600 }}>{slotDt.minutes} min</div>
                          <div style={{ fontSize: 10, color: '#666666' }}>{slotDt.reason_name}</div>
                          <button onClick={() => setDtModal({ open: true, slot })} style={{ fontSize: 11, padding: '3px 7px', borderRadius: 4, border: '0.5px solid rgba(0,0,0,0.10)', background: '#fff', cursor: 'pointer', color: '#666666' }}>Edit</button>
                          <button onClick={() => handleDtClear(slot.slot_id)} style={{ fontSize: 11, padding: '3px 7px', borderRadius: 4, border: '0.5px solid #A32D2D', background: '#FCEBEB', cursor: 'pointer', color: '#A32D2D' }}>Clear</button>
                        </div>
                      ) : (
                        <button onClick={() => setDtModal({ open: true, slot })} style={{
                          padding: '6px 12px', borderRadius: 6, border: '1.5px solid rgba(0,0,0,0.10)',
                          background: '#fff', cursor: 'pointer', fontSize: 12, color: '#666666', fontWeight: 600
                        }}>+ Downtime</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}